import { useEffect, useState } from 'react';

// <input type="number"> segue o idioma do navegador: em en-US a vírgula é
// ignorada e "11,99" vira 1199. Campos numéricos usam texto com teclado
// decimal: aceitam vírgula ou ponto, exibem com vírgula e entregam o valor com
// ponto (formato esperado por parseFloat/Number e pela API).

/** Normaliza o que foi digitado para "1234.56". */
export function normalizeDecimal(value: string, allowNegative = true): string {
  const raw = String(value ?? '');
  const negative = allowNegative && raw.trim().startsWith('-');
  const cleaned = raw.replace(/[^\d.,]/g, '').replace(/\./g, ',');
  const [integer, ...decimals] = cleaned.split(',');
  const number = decimals.length ? `${integer}.${decimals.join('')}` : integer;
  return negative ? `-${number}` : number;
}

/** Valor do estado ("1234.56" ou número) para exibição ("1234,56"). */
export function toDecimalDisplay(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  return String(value).replace('.', ',');
}

/** Campo com min >= 0 não aceita sinal negativo. */
export function allowsNegative(min?: string | number): boolean {
  return min === undefined || min === '' || Number(min) < 0;
}

/**
 * Troca o valor do evento pelo normalizado, para handlers que leem
 * `e.target.value`.
 */
export function normalizeDecimalEvent<E extends { target: { value: string } }>(
  e: E,
  allowNegative = true
): E {
  e.target.value = normalizeDecimal(e.target.value, allowNegative);
  return e;
}

/** Props que substituem type="number" no elemento. */
export const decimalInputAttrs = {
  type: 'text',
  inputMode: 'decimal' as const,
};

const asNumber = (value: unknown): number => {
  const parsed = parseFloat(normalizeDecimal(String(value ?? '')));
  return Number.isNaN(parsed) ? NaN : parsed;
};

const isBlank = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  ['', '-'].includes(String(value).trim());

// Campo vazio é compatível com 0/NaN: handlers como Number('') devolvem 0, e
// sem isso o campo voltaria a "0" ao ser apagado ("011,99" ao redigitar).
const sameNumber = (text: unknown, value: unknown): boolean => {
  const y = asNumber(value);
  if (isBlank(text)) return isBlank(value) || y === 0 || Number.isNaN(y);
  const x = asNumber(text);
  return (Number.isNaN(x) && Number.isNaN(y)) || x === y;
};

/**
 * Estado de exibição de um campo decimal controlado.
 *
 * Guarda o texto digitado ("11," enquanto o usuário digita) e só sincroniza
 * com `value` quando o número difere — mesma regra do React para
 * type="number". Assim funciona com handlers que guardam a string e com os que
 * convertem na hora (`Number(e.target.value)`).
 */
export function useDecimalInput(
  value: unknown,
  onChange: ((e: any) => void) | undefined,
  options: { enabled?: boolean; min?: string | number } = {}
) {
  const { enabled = true, min } = options;
  const [text, setText] = useState<string>(() => toDecimalDisplay(value));

  useEffect(() => {
    if (enabled && !sameNumber(text, value)) {
      setText(toDecimalDisplay(value));
    }
  });

  const handleChange = (e: any) => {
    normalizeDecimalEvent(e, allowsNegative(min));
    setText(toDecimalDisplay(e.target.value));
    onChange?.(e);
  };

  return {
    ...decimalInputAttrs,
    value: text,
    onChange: handleChange,
  };
}
